/** @file World transform of one bone from its local values, parent world and inherit mode. */
import { affine } from '@pose2d/math/affine';
import type { Affine } from '@pose2d/math/types';
import { POSE_LAYOUT } from '../contracts/pose';
import type { InheritMode } from '../skeletonData';
import { effectiveParent } from './effectiveParent';

/** Skeleton space: a root's parent is the identity, for which every inherit mode gives E = I, so W = L. */
const ROOT: Affine = [1, 0, 0, 1, 0, 0];
/** Module scratch (local matrix, effective parent); never escapes, keeps the per-frame path allocation-free. */
const lm = new Float64Array(POSE_LAYOUT.WORLD_STRIDE);
const em = new Float64Array(POSE_LAYOUT.WORLD_STRIDE);

/**
 * out = effectiveParent(parent, inherit) x local(local[offset..offset+7]); parent null = skeleton root.
 * The linear part is E·L₂ (spec §6.2); the origin always goes through the full parent: P·(x, y) + p.
 * Alias-safe: out may be parent.
 */
export function localToWorld(out: Affine, local: Float64Array, offset: number, parent: Affine | null, inherit: InheritMode): Affine {
  const p = parent ?? ROOT;
  const pa = p[0] ?? 0;
  const pb = p[1] ?? 0;
  const pc = p[2] ?? 0;
  const pd = p[3] ?? 0;
  const px = p[4] ?? 0;
  const py = p[5] ?? 0;
  const x = local[offset + POSE_LAYOUT.X] ?? 0;
  const y = local[offset + POSE_LAYOUT.Y] ?? 0;
  affine.fromLocal(
    lm, x, y,
    local[offset + POSE_LAYOUT.ROTATION] ?? 0,
    local[offset + POSE_LAYOUT.SCALE_X] ?? 0,
    local[offset + POSE_LAYOUT.SCALE_Y] ?? 0,
    local[offset + POSE_LAYOUT.SHEAR_X] ?? 0,
    local[offset + POSE_LAYOUT.SHEAR_Y] ?? 0,
  );
  effectiveParent(em, p, inherit);
  affine.mul(out, em, lm);
  out[4] = pa * x + pb * y + px;
  out[5] = pc * x + pd * y + py;
  return out;
}
